/**
 * React Hook: Deployment Progress SSE
 * 
 * Subscribes to real-time deployment progress updates via Server-Sent Events (SSE)
 * 
 * Uses the centralized SSE client from dataProvider for:
 * - Automatic token injection
 * - Base URL handling
 * - Consistent error handling
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createSSEConnection, type SSEConnection } from '@/dataProvider';
import { useAuthStoreV1 } from '@/stores/authStore_v1';

/**
 * Deployment progress event from SSE stream
 */
export interface DeploymentProgressEvent {
  deployment_id: string;
  status: 'queued' | 'in_progress' | 'succeeded' | 'failed' | 'cancelled';
  message: string | null;
  async_process_id: string | null;
  error_message: string | null;
  timestamp: string;
}

/**
 * Hook return type
 */
export interface UseDeploymentProgressReturn {
  /** Current deployment status */
  status: DeploymentProgressEvent['status'] | null;
  /** Human-readable status message */
  message: string | null;
  /** Error message if deployment failed */
  errorMessage: string | null;
  /** Whether deployment has reached terminal state */
  isComplete: boolean;
  /** Whether SSE connection is active */
  isConnected: boolean;
  /** Connection error */
  connectionError: string | null;
  /** Full progress event data */
  progressEvent: DeploymentProgressEvent | null;
  /** Connect to deployment progress stream */
  connect: (deploymentId: string) => void;
  /** Disconnect from stream */
  disconnect: () => void;
}

/**
 * Terminal states that indicate deployment is complete
 */
const TERMINAL_STATES: DeploymentProgressEvent['status'][] = ['succeeded', 'failed', 'cancelled'];

/**
 * Hook to subscribe to deployment progress updates via SSE
 * 
 * @returns Deployment progress state and controls
 * 
 * @example
 * ```tsx
 * const { status, message, isComplete, connect, disconnect } = useDeploymentProgress();
 * 
 * // After initiating deployment
 * const result = await deployMutation.mutateAsync({...});
 * connect(result.id); // Start listening to progress
 * 
 * // Clean up on unmount handled automatically
 * ```
 */
export function useDeploymentProgress(): UseDeploymentProgressReturn {
  const [status, setStatus] = useState<DeploymentProgressEvent['status'] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [progressEvent, setProgressEvent] = useState<DeploymentProgressEvent | null>(null);

  // SSE connection ref
  const connectionRef = useRef<SSEConnection | null>(null);

  /**
   * Disconnect from SSE stream
   */
  const disconnect = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.abort();
      connectionRef.current = null;
    }
    setIsConnected(false);
  }, []);

  /**
   * Reset state for new connection
   */
  const resetState = useCallback(() => {
    setStatus(null);
    setMessage(null);
    setErrorMessage(null);
    setIsComplete(false);
    setConnectionError(null);
    setProgressEvent(null);
  }, []);

  /**
   * Connect to deployment progress SSE stream
   */
  const connect = useCallback((deploymentId: string) => {
    if (!deploymentId) {
      setConnectionError('No deployment ID provided');
      return;
    }

    // Disconnect any existing connection
    disconnect();
    resetState();

    // Create SSE connection using centralized client
    const authState = useAuthStoreV1.getState();
    const tenantHeaders: Record<string, string> = {};
    if (authState.selectedOrg?.id) tenantHeaders['X-Organization-ID'] = authState.selectedOrg.id;
    if (authState.user?.id) tenantHeaders['X-User-ID'] = authState.user.id;

    connectionRef.current = createSSEConnection<DeploymentProgressEvent>(
      `/deployments/${deploymentId}/progress`,
      (data) => {
        // Update state with received event
        setProgressEvent(data);
        setStatus(data.status);
        setMessage(data.message);

        if (data.error_message) {
          setErrorMessage(data.error_message);
        }

        // Check if deployment is complete
        if (TERMINAL_STATES.includes(data.status)) {
          setIsComplete(true);
          setIsConnected(false);
          // Connection will auto-close from server side
        }
      },
      {
        headers: tenantHeaders,
        onOpen: () => {
          setIsConnected(true);
          setConnectionError(null);
        },
        onError: (error) => {
          setConnectionError(error.message || 'Connection failed');
          setIsConnected(false);
        },
        onClose: () => {
          setIsConnected(false);
        },
      }
    );
  }, [disconnect, resetState]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    status,
    message,
    errorMessage,
    isComplete,
    isConnected,
    connectionError,
    progressEvent,
    connect,
    disconnect,
  };
}
